<template>
  <div class="after-sale-contract">
    <card-m cardWidth="100%" cardHeight="100%"
      :show-export="true"
      export-file-name="合同售后数据"
      @export="handleExport"
    >
      <template slot="card-title">
        <AppBreadcrumb
          page-name="合同售后"
          :list="breadcrumbList"
        />
      </template>

      <template slot="card-box">
        <div class="dashboard">
          <div class="left">
            <after-sale-no-table
              tableId="afterSaleContractNoTable"
              :noList="afterSaleNoList"
              :currentNo="currentAfterSaleNo"
              :loading="noTableLoading"
              @select="handleSelectNo"
            />
          </div>
          <div class="right">
            <!-- ✅ 使用 service-and-pick 组件 -->
            <service-and-pick
              tableId="afterSaleContractDetail"
              :serviceList="currentServiceList"
              :pickList="currentPickList"
              :serviceTotalCount="currentServiceTotalCount"
              :pickTotalCount="currentPickTotalCount"
              :loading="detailLoading"
            />
          </div>
        </div>
      </template>
    </card-m>
  </div>
</template>

<script>
import afterSaleNoTable from './afterSaleNoTable.vue'
import serviceAndPick from './serviceAndPick.vue'
import { getAfterSaleNoFromContract, getAfterSaleInfoFromNo } from '@/api/mainapi.js';
import FileSaver from 'file-saver'
import XLSX from 'xlsx'

export default {
  name: 'afterSaleContract',
  components: { afterSaleNoTable, serviceAndPick },
  data() {
    return {
      contractNo: this.$route.params.contractId,
      projectName: this.$route.params.projectName || '', // ✅ 新增
      afterSaleNoList: [],
      currentAfterSaleNo: '',

      // ✅ 后端数据没回来时表格转圈（与 contractProgress 的表单 loading 一致）
      noTableLoading: false,   // 左侧：售后编号列表
      detailLoading: false,    // 右侧：服务 / 领料明细

      // ✅ 新增：当前售后编号的详细信息
      currentServiceList: [],
      currentPickList: [],
      currentServiceTotalCount: 0,
      currentPickTotalCount: 0
    }
  },
computed: {
  /** ✅ 合同维度 */
  isFromContract() {
    return this.$route.name === '合同售后信息1';
  },
  projectNo() {
    return this.$route.params.projectNo || '';
  },

  /** ✅ 从缓存读项目名称 */
  projectNameFromCache() {
    if (!this.projectNo) return ''
    return sessionStorage.getItem(`project_name_${this.projectNo}`) || ''
  },

  /** ✅ 从缓存读合同名称 */
  contractNameFromCache() {
    if (!this.contractNo) return ''
    return sessionStorage.getItem(`contract_name_${this.contractNo}`) || this.contractNo
  },

  /** ✅ 项目维度 */
  isFromProject() {
    return !!this.projectNo;   // ✅ 只认 projectNo
  },

  /** ✅ 面包屑数据 */
  breadcrumbList() {
    const list = [];

    // ✅ 首页
    list.push({
      label: this.isFromProject ? '项目主页' : '合同主页',
      to: this.isFromProject ? '/homepage/projects' : '/homepage/contracts'
    });

    // ✅ 项目维度：项目名
    if (this.isFromProject) {
      list.push({
        label: this.projectNameFromCache || this.projectNo,
        tooltip: this.projectNameFromCache || this.projectNo,
        to: `/homepage/project/${encodeURIComponent(this.projectNo)}`
      });
    }

    // ✅ 合同号（显示缓存名称）
    list.push({
      label: this.contractNameFromCache,
      tooltip: this.contractNameFromCache,
      to: this.isFromProject
        ? {
            name: '合同信息1',
            params: {
              projectName: this.projectName,
              contractId: this.contractNo
            }
          }
        : {
            name: '合同信息',
            params: { contractId: this.contractNo }
          }
    });

    // ✅ 当前售后（不可点）
    list.push({
      label: '合同售后',
      tooltip: '合同售后',
      disabled: true
    });

    return list;
  }
},
  mounted() {
    this.fetchAfterSaleNos()
  },
  methods: {
    async fetchAfterSaleNos() {
      this.noTableLoading = true;
      try {
        const res = await getAfterSaleNoFromContract({
          contractNo: this.contractNo,
          Paging_pageNumber: 1,
          Paging_pageSize: 100
        })

        if (res.data.code !== 200) return

        const data = res.data.data || {}
        const pickList = data.pickNumList || []
        const serviceList = data.serviceNumList || []

        // ✅ 不区分来源，直接合并
        this.afterSaleNoList = [...pickList, ...serviceList]
        
        // ✅ 默认选中第一个编号
        if (this.afterSaleNoList.length > 0) {
          this.handleSelectNo(this.afterSaleNoList[0])
        }
      } catch (e) {
        console.error(e)
      } finally {
        // 无论成功 / 失败 / 提前 return，都要关掉转圈
        this.noTableLoading = false;
      }
    },

    async handleSelectNo(row) {
      this.currentAfterSaleNo = row['编号']
      this.detailLoading = true;
      
      // ✅ 调用接口获取该售后编号的详细信息
      try {
        const res = await getAfterSaleInfoFromNo({
          id: row['编号'],
          pageSize: 100, // 尽可能最大
          pageNo: 1
        })

        if (res.data.code !== 200) {
          this.$message.error(res.data.message || '获取售后信息失败')
          return
        }

        const data = res.data.data || {}
        
        // ✅ 更新右侧 service-and-pick 组件的数据
        this.currentServiceList = data.serviceList || []
        this.currentPickList = data.pickList || []
        this.currentServiceTotalCount = Number(data['服务总条数'] || 0)
        this.currentPickTotalCount = Number(data['领料总条数'] || 0)
        
      } catch (e) {
        console.error(e)
        this.$message.error('获取售后信息失败')
      } finally {
        this.detailLoading = false;
      }
    },

    /** 导出Excel：售后编号 + 服务/领料明细合并为一个 Excel */
    handleExport() {
      const xlsxParam = { raw: true };
      const wb = XLSX.utils.book_new();

      // Sheet 1: 售后编号列表
      const noEl = document.querySelector('#afterSaleContractNoTable');
      if (noEl) {
        const noSheet = XLSX.utils.table_to_sheet(noEl, xlsxParam);
        XLSX.utils.book_append_sheet(wb, noSheet, '售后编号');
      }

      // Sheet 2: 服务/领料明细
      const detailEl = document.querySelector('#afterSaleContractDetail');
      if (detailEl) {
        const detailSheet = XLSX.utils.table_to_sheet(detailEl, xlsxParam);
        XLSX.utils.book_append_sheet(wb, detailSheet, '售后明细');
      }

      const wbout = XLSX.write(wb, {
        bookType: 'xlsx',
        bookSST: true,
        type: 'array'
      });

      try {
        FileSaver.saveAs(
          new Blob([wbout], { type: 'application/octet-stream' }),
          '合同售后数据.xlsx'
        );
      } catch (e) {
        if (typeof console !== 'undefined') {
          console.log(e, wbout);
        }
      }
      return wbout;
    }
  }
}
</script>

<style scoped lang="scss">
.dashboard {
  height: calc(100vh - 130px);
  display: grid;
  grid-template-columns: 2fr 3fr;
  gap: 1px;
}

.left, .right {
  height: 100%;
  min-height: 0;        /* ✅ 关键 */
  overflow: hidden;     /* ✅ 关键 */
}
</style>